import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuthState, useDataProvider, useNotify, useRedirect } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const WorkflowIcon = AccountTreeIcon;

interface BrandWorkflow {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  _count?: {
    statuses: number;
    categories: number;
  };
}

export const BrandWorkflowList = () => {
  const { identity } = useAuthState();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  // Load brand selection from localStorage on mount
  const [selectedBrandId, setSelectedBrandId] = useState<string>(() => {
    return localStorage.getItem('selectedBrandIdForWorkflows') || '';
  });
  const [brands, setBrands] = useState<any[]>([]);
  const [workflows, setWorkflows] = useState<BrandWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBrandName, setSelectedBrandName] = useState<string>('');

  // Also check localStorage as fallback
  const storedUser = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const isEnterpriseAdmin =
    (identity as any)?.isEnterpriseAdmin === true ||
    storedUser?.isEnterpriseAdmin === true;

  // Fetch brands when component mounts
  React.useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await dataProvider.getList('brands', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setBrands(response.data);
      } catch (error) {
        notify('Failed to load brands', { type: 'error' });
      }
    };

    if (isEnterpriseAdmin) {
      fetchBrands();
    }
  }, [isEnterpriseAdmin, dataProvider, notify]);

  // Fetch workflows when brand is selected
  React.useEffect(() => {
    if (selectedBrandId) {
      const fetchWorkflows = async () => {
        setLoading(true);
        try {
          const response = await dataProvider.getList('brand-workflows', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: { brandId: selectedBrandId },
          });
          setWorkflows(response.data);
        } catch (error) {
          notify('Failed to load workflows', { type: 'error' });
          setWorkflows([]);
        } finally {
          setLoading(false);
        }
      };

      fetchWorkflows();
    }
  }, [selectedBrandId, dataProvider, notify]);

  const handleBrandChange = (brandId: string) => {
    setSelectedBrandId(brandId);
    localStorage.setItem('selectedBrandIdForWorkflows', brandId);
    setWorkflows([]);

    // Update brand name for breadcrumbs
    const brand = brands.find(b => b.id === brandId);
    setSelectedBrandName(brand?.name || '');
  };

  const handleCreate = () => {
    if (!selectedBrandId) {
      notify('Please select a brand first', { type: 'warning' });
      return;
    }
    redirect(`/brand-workflows/create?brandId=${selectedBrandId}`);
  };

  const handleRowClick = (workflow: BrandWorkflow) => {
    redirect(`/brand-workflows/${workflow.id}/show`);
  };

  if (!isEnterpriseAdmin) {
    return (
      <Box>
        <PageTitle
          title="Brand Workflows"
          subtitle="Manage workflows for brands"
          icon={<WorkflowIcon />}
        />
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography color="warning">
              Only enterprise administrators can manage brand workflows
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <Link
          color="inherit"
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => redirect('/configuration/enterprise-admin')}
        >
          Configuration
        </Link>
        <Typography color="text.primary">Brand Workflows</Typography>
        {selectedBrandName && <Typography color="text.primary">{selectedBrandName}</Typography>}
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => redirect('/configuration/enterprise-admin')}
          variant="outlined"
        >
          Back
        </Button>
        <Box>
          <PageTitle
            title="Brand Workflows"
            subtitle="Manage operational workflows for brand franchisees"
            icon={<WorkflowIcon />}
          />
        </Box>
      </Box>

      {/* Brand Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              select
              label="Select Brand"
              value={selectedBrandId}
              onChange={(e) => handleBrandChange(e.target.value)}
              sx={{ minWidth: 300 }}
              disabled={brands.length === 0}
            >
              <MenuItem value="">-- Choose a brand --</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={!selectedBrandId}
              sx={{ mt: 1 }}
            >
              Create Workflow
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Workflows List */}
      {selectedBrandId && (
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : workflows.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                No workflows found for this brand
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Statuses</TableCell>
                      <TableCell align="center">Categories</TableCell>
                      <TableCell align="center">Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workflows.map((workflow) => (
                      <TableRow
                        key={workflow.id}
                        hover
                        onClick={() => handleRowClick(workflow)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{workflow.name}</TableCell>
                        <TableCell>{workflow.description || '-'}</TableCell>
                        <TableCell align="center">{workflow._count?.statuses || 0}</TableCell>
                        <TableCell align="center">{workflow._count?.categories || 0}</TableCell>
                        <TableCell align="center">
                          {workflow.isActive ? (
                            <Chip label="Active" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Inactive" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

    </Box>
  );
};
